import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, CreditCard, Trash2, ChevronLeft } from 'lucide-react';
import Layout from '../components/Layout';
import { vault } from '../services/api';
import { useToast } from '../context/ToastContext';

interface AccountRow {
  id: number;
  bank_name: string | null;
  nickname: string | null;
  account_type: string | null;
  account_number_masked: string | null;
}

const VaultProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);
  const navigate = useNavigate();
  const { toast, confirm } = useToast();
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [bankName, setBankName] = useState('');
  const [nickname, setNickname] = useState('');

  const load = () => {
    setLoading(true);
    vault.getAccounts(profileId).then(r => setAccounts(r.data)).catch(() => toast('Unable to load bank accounts', 'error')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [profileId]);

  const handleCreate = async () => {
    if (!bankName.trim() && !nickname.trim()) { toast('Provide at least a bank name or nickname', 'error'); return; }
    try {
      const res = await vault.createAccount(profileId, { bank_name: bankName, nickname });
      setShowForm(false);
      setBankName(''); setNickname('');
      navigate(`/vault/accounts/${res.data.id}`);
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Unable to create bank account', 'error');
    }
  };

  const handleDelete = async (a: AccountRow) => {
    const ok = await confirm(`Delete "${a.nickname || a.bank_name}"? This permanently deletes its cards, net banking, and other vault data.`);
    if (!ok) return;
    try { await vault.deleteAccount(a.id); load(); } catch { toast('Unable to delete bank account', 'error'); }
  };

  return (
    <Layout title="Bank Accounts" subtitle="">
      <button onClick={() => navigate('/vault')} style={backLinkStyle}><ChevronLeft size={14} /> Back to Vault</button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '16px 0 20px' }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{accounts.length} bank account{accounts.length === 1 ? '' : 's'}</div>
        <button onClick={() => setShowForm(true)} style={primaryButtonStyle}><Plus size={16} /> Add Bank Account</button>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      ) : accounts.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', padding: 40, textAlign: 'center' }}>No bank accounts yet.</div>
      ) : (
        <div className="grid-3">
          {accounts.map(a => (
            <div key={a.id} onClick={() => navigate(`/vault/accounts/${a.id}`)} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <CreditCard size={20} color="var(--accent-blue)" />
                <button onClick={e => { e.stopPropagation(); handleDelete(a); }} style={iconButtonStyle}><Trash2 size={14} /></button>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 12 }}>{a.bank_name || a.nickname}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{a.account_type || 'Account'} {a.account_number_masked || ''}</div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 28, maxWidth: 400, width: '90%' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Add Bank Account</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>You can fill in the rest of the details (account number, cards, net banking) after creating it.</div>
            <input value={bankName} onChange={e => setBankName(e.target.value)} placeholder="Bank name (e.g. HDFC Bank)" style={inputStyle} />
            <input value={nickname} onChange={e => setNickname(e.target.value)} placeholder="Nickname (optional)" style={{ ...inputStyle, marginTop: 10 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setShowForm(false)} style={cancelButtonStyle}>Cancel</button>
              <button onClick={handleCreate} style={primaryButtonStyle}>Create</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14 };
const primaryButtonStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'var(--accent-blue)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const cancelButtonStyle: React.CSSProperties = { padding: '9px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconButtonStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, padding: 6, cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' };
const cardStyle: React.CSSProperties = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, cursor: 'pointer', boxShadow: 'var(--shadow-card)' };
const backLinkStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', padding: 0 };

export default VaultProfile;
